// Single rule editor — toggle + common fields + rule-specific params slot.
//
// Renders a collapsible card for one rule type. When disabled, shows just
// a label and toggle. When enabled, expands to show enforcement, action,
// value, warning threshold, and any rule-specific params passed as children.

import { For, Show, type JSX } from 'solid-js';
import type { Authority } from '../form';
import { authorConsequences, countsRuleOccurrences, type RuleTypeConfig, type RuleBuilderState } from '../core/ruleRegistry';
import Dropdown from './Dropdown';
import { RULE_PARAMS_WIRE, ruleAuthoringRefusal } from '../core/ruleParamsWire';
import NumberInput from './NumberInput';
import ScopePicker from './ScopePicker';
import { useMoneyMark } from './builderWordsContext';

interface RuleToggleEditorProps {
  authority: Authority;
  rule: RuleBuilderState;
  config: RuleTypeConfig;
  onUpdate: (partial: Partial<RuleBuilderState>) => void;
  onToggle: (enabled: boolean) => void;
  /** Present on an ADDED scoped row: removing it is the only way back, because
   *  a scoped row has no "off" state that keeps its bucket. */
  onRemove?: () => void;
  children?: JSX.Element;
}

export default function RuleToggleEditor(props: RuleToggleEditorProps) {
  const mark = useMoneyMark();
  /** An absolute limit of a money family is an amount of the template's currency. */
  const money = () => RULE_PARAMS_WIRE[props.config.key]?.money !== undefined && props.rule.valueMode === 'absolute';
  return (
    <div class={`ch-rule-toggle-card${props.rule.enabled ? ' enabled' : ''}`}>
      {/* Toggle row */}
      <Show when={props.config.coreInline}><strong>{props.config.label} consequences</strong></Show>
      <Show when={!props.config.coreInline}>
        <label class="ch-rule-toggle-row">
          <input
            type="checkbox"
            checked={props.rule.enabled}
            disabled={!props.config.implemented}
            onChange={(e) => props.onToggle(e.currentTarget.checked)}
          />
          <div class="ch-rule-toggle-info">
            <span class="ch-rule-toggle-label">{props.config.label}</span>
            <span class="ch-rule-toggle-desc">{props.config.description}</span>
          </div>
          <Show when={!props.config.implemented}>
            <span class="ch-rule-badge-upcoming">{ruleAuthoringRefusal(props.rule)}</span>
          </Show>
          {/* Implemented, but depending on a feed the platform does not yet
              produce: enforcing it would promise a trader something that never
              fires, and the publish gate refuses exactly that. */}
          <Show when={props.config.availabilityNote}>
            <span class="ch-rule-badge-unavailable" title="Enforce mode cannot be published until this feed ships; warn mode works today.">
              {props.config.availabilityNote}
            </span>
          </Show>
          <Show when={props.onRemove}>
            <button type="button" class="ch-rule-scope-remove" aria-label="Remove scoped limit"
              onClick={() => props.onRemove?.()}>Remove</button>
          </Show>
        </label>
      </Show>

      {/* Expanded config (only when enabled) */}
      <Show when={props.rule.enabled}>
        <div class="ch-rule-config">
          {/* Common fields */}
          <div class="ch-rule-common-row">
            <div class="ch-field ch-field-sm">
              {/* ONE PICKER. Enforcement is the consequence: a rung that blocks
                  is an enforcing rule and a rung that flags is not. */}
              <span class="ch-label">First occurrence</span>
              <Dropdown
                value={props.rule.action}
                options={[{ value: '', label: 'Choose consequence' },
                  ...authorConsequences(props.rule.timing, props.config.key, props.authority)]}
                onChange={(v) => props.onUpdate({ action: v })}
              />
            </div>
            <Show when={props.config.requiresValue && !props.config.coreInline}>
              <div class="ch-field ch-field-sm">
                <span class="ch-label">Value</span>
                <NumberInput
                  value={props.rule.value}
                  onInput={(v) => props.onUpdate({ value: v })}
                  min={0}
                  step={0.1}
                  prefix={money() ? mark : undefined}
                />
              </div>
              <Show when={props.config.validValueModes.length > 1}>
                <div class="ch-field ch-field-sm">
                  <span class="ch-label">Mode</span>
                  <Dropdown
                    value={props.rule.valueMode}
                    options={props.config.validValueModes.map((m) => ({
                      value: m,
                      label: m.charAt(0).toUpperCase() + m.slice(1),
                    }))}
                    onChange={(v) => props.onUpdate({ valueMode: v })}
                  />
                </div>
              </Show>
            </Show>
            <div class="ch-field ch-field-sm">
              <span class="ch-label">Warn at %</span>
              <NumberInput
                value={props.rule.warningThresholdPct}
                onInput={(v) => props.onUpdate({ warningThresholdPct: v })}
                min={0}
                max={100}
                step={5}
              />
            </div>
          </div>

          <Show when={countsRuleOccurrences(props.rule.timing)}>
            <p>Each repeat applies from its occurrence onward, until the next rung. The last rung keeps applying.</p>
            <For each={props.rule.repeats}>{(rung, index) => (
              <div class="ch-rule-common-row">
                <label>From occurrence
                  <input
                    class="ch-input"
                    type="number"
                    min="2"
                    max="4294967295"
                    step="1"
                    aria-label="Repeat from occurrence"
                    value={rung[0]}
                    onInput={(e) => props.onUpdate({
                      repeats: props.rule.repeats.map((repeat, i) => {
                        const [, action] = repeat;
                        return i === index() ? [Number(e.currentTarget.value), action] : repeat;
                      }),
                    })}
                  />
                </label>
                <Dropdown
                  value={rung[1]}
                  options={[
                    { value: '', label: 'Choose consequence' },
                    ...authorConsequences(props.rule.timing, props.config.key, props.authority),
                  ]}
                  onChange={(action) => props.onUpdate({
                    repeats: props.rule.repeats.map((repeat, i) => {
                      const [occurrence] = repeat;
                      return i === index() ? [occurrence, action] : repeat;
                    }),
                  })}
                />
                <button
                  type="button"
                  onClick={() => props.onUpdate({
                    repeats: props.rule.repeats.filter((_, i) => i !== index()),
                  })}
                >Remove repeat</button>
              </div>
            )}</For>
            <button
              type="button"
              disabled={props.rule.repeats.length >= 32 || (props.rule.repeats.at(-1)?.[0] ?? 1) >= 4294967295}
              onClick={() => {
                const occurrence = (props.rule.repeats.at(-1)?.[0] ?? 1) + 1;
                props.onUpdate({ repeats: [...props.rule.repeats, [occurrence, '']] });
              }}
            >Add repeat</button>
          </Show>
          <Show when={!countsRuleOccurrences(props.rule.timing)}>
            <p>Repeats are unavailable because this rule does not count occurrences.</p>
          </Show>
          <Show when={ruleAuthoringRefusal(props.rule)}>
            {(refusal) => <p role="alert">{refusal()}</p>}
          </Show>

          {/* The bucket. Only the families with bucket arithmetic offer one;
              for the rest the account is the only honest answer, so no picker
              is shown rather than one that is always disabled. */}
          <Show when={props.config.scopable}>
            <ScopePicker
              scope={props.rule.scope}
              units={props.rule.valueMode || props.config.defaultValueMode || 'lots'}
              onChange={(scope) => props.onUpdate({ scope })}
            />
          </Show>

          {/* Rule-specific params (passed as children) */}
          <Show when={props.children}>
            <div class="ch-rule-params">{props.children}</div>
          </Show>
        </div>
      </Show>
    </div>
  );
}
